import sdmxApi from '../../api/sdmx';
import * as R from 'ramda';
import { select, put, call, takeLatest } from 'redux-saga/effects';
import {
  getRequestArgs as sdmxjsGetRequestArgs,
  getExternalResources,
  parseExternalReference,
} from '@sis-cc/dotstatsuite-sdmxjs';
import { getLocale } from '../../selectors/router';
import {
  HANDLE_EXTERNAL_RESOURCES,
  REQUEST_EXTERNAL_RESOURCES,
} from '../../ducks/sdmx';
import {
  setPending,
  pushLog,
  flushLog,
  LOG_ERROR_SDMX_STRUCTURE,
} from '../../ducks/app';
import { getSpaceFromDatasourceId, getSpaceFromUrl } from '../../lib/settings';
import { renameKeys } from '../../utils';

const isDev = process.env.NODE_ENV === 'development';

function* requestExternalResources({ dataflow }) {
  const dataflowId = R.prop('dataflowId')(dataflow);
  const pendingId = `getExternalResources/${dataflowId}`;
  const locale = yield select(getLocale);
  const externalUrl = R.prop('externalUrl', dataflow);
  const externalReference = parseExternalReference(
    {
      isExternalReference: !R.isNil(externalUrl),
      links: [{ rel: 'external', href: externalUrl }],
    },
    'dataflow',
  );

  const space = R.isNil(externalReference)
    ? getSpaceFromDatasourceId(R.prop('datasourceId', dataflow))
    : getSpaceFromUrl(R.path(['datasource', 'url'], externalReference));

  const datasource = space || R.prop('datasource', externalReference);
  const requestArgs = sdmxjsGetRequestArgs({
    identifiers: R.defaultTo(
      R.pipe(
        R.pickAll(['agencyId', 'dataflowId', 'version']),
        renameKeys({ dataflowId: 'code' }),
      )(dataflow),
      R.prop('identifiers', externalReference),
    ),
    datasource,
    type: 'dataflow',
    withPartialReferences: true,
    locale,
  });

  try {
    yield put(setPending(pendingId, true));
    yield put(flushLog(LOG_ERROR_SDMX_STRUCTURE));
    if (isDev) console.info(`request: ${pendingId}`); // eslint-disable-line no-console
    const structure = yield call(sdmxApi, {
      method: 'getStructureExternalResources',
      requestArgs,
    });
    yield put(setPending(pendingId, false));
    yield put({
      type: HANDLE_EXTERNAL_RESOURCES,
      externalResources: { [dataflowId]: getExternalResources(structure) },
    });
  } catch (error) {
    yield put(setPending(pendingId, false));

    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      const log = {
        status: error.response.status,
        statusText: error.response.statusText,
        url: requestArgs.url,
        headers: requestArgs.headers,
      };
      yield put(pushLog({ type: LOG_ERROR_SDMX_STRUCTURE, log }));
    } else if (error.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
      // http.ClientRequest in node.js
      console.log(error.request); // eslint-disable-line no-console
      yield put(pushLog({ type: LOG_ERROR_SDMX_STRUCTURE, log: error }));
    } else {
      // Something happened in setting up the request that triggered an Error
      console.log('Error', error.message); // eslint-disable-line no-console
      yield put(pushLog({ type: LOG_ERROR_SDMX_STRUCTURE, log: error }));
    }
  }
}

export function* watchRequestStructure() {
  yield takeLatest(REQUEST_EXTERNAL_RESOURCES, requestExternalResources);
}
